import { useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '@/store/reduxHooks';
import { syncUnviewed } from '@/store/unviewedOrders';
import { pruneStale, syncHistory } from '@/store/notifications';
import { tabTitleNotifier } from '@/services/tabNotifications/tabTitleNotifier';
import {
  applyBadge,
  resetBadge
} from '@/services/tabNotifications/faviconBadge';
import { UNVIEWED_ORDERS_KEY } from '@/services/mercure/unviewedOrdersStorage';
import { NOTIFICATIONS_HISTORY_KEY } from '@/services/notifications/notificationsHistoryStorage';

/**
 * Индикация вкладки: счётчик непросмотренных заказов в `document.title` и
 * красный колокольчик-бейдж на favicon. Источник правды — слайс
 * `unviewedOrders`, индикация применяется декларативно от его размера.
 *
 * Возврат фокуса на вкладку (`visibilitychange`) и изменение реестра в другой
 * вкладке (`storage`) пересинхронизируют стейт с localStorage.
 */
export const useTabNotification = (): void => {
  const dispatch = useAppDispatch();
  const count = useAppSelector((state) => state.unviewedOrders.uuids.length);

  useEffect(() => {
    tabTitleNotifier.sync(count);

    if (count > 0) {
      applyBadge(count);
    } else {
      resetBadge();
    }
  }, [count]);

  useEffect(() => {
    const handleVisibilityChange = (): void => {
      if (document.visibilityState === 'visible') {
        dispatch(syncUnviewed());
        dispatch(syncHistory());
        dispatch(pruneStale());
      }
    };

    const handleStorage = (event: StorageEvent): void => {
      if (event.key === UNVIEWED_ORDERS_KEY) {
        dispatch(syncUnviewed());
      }
      if (event.key === NOTIFICATIONS_HISTORY_KEY) {
        dispatch(syncHistory());
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('storage', handleStorage);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('storage', handleStorage);
    };
  }, [dispatch]);
};
